import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Info } from 'lucide-react'

import { Text } from '@/components/ui/Text'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { StatTabs } from '@digital-twins/deck-ui/plots/_shared'

import { SummaryItemSelector } from './SummaryItemSelector'
import { NPS_POINTS_UNIT, formatAbsoluteScore, formatLiftTriplet } from './formatAbsoluteScore'

import {
  LiftScorecard,
  LiftScorecardAssetScore,
  LiftScorecardRow,
  LiftStatFigures,
  LiftStatTest,
  ScorecardDirection,
} from '@digital-twins/shared-types'

export interface LiftViewProps {
  scorecard: LiftScorecard
  itemTags: Record<string, string>
  /** Shows the "Lift" heading. The Survey data card already names its view. */
  showHeading?: boolean
}

type NotApplicableBefore = NonNullable<LiftScorecardAssetScore['notApplicableBefore']>

/**
 * Decision 20: the statistic the card reads. `mean` is the Average (the
 * row's own pre/post/delta); `topBoxes` the Top N share; `nps` the NPS score.
 */
type LiftStatView = 'mean' | 'topBoxes' | 'nps'

/** The figures one row shows: the selected asset's, else the pooled ones. */
interface LiftRowValues {
  pre: number
  post: number
  delta: number
  direction: ScorecardDirection
  pairs: number | null
  lowBase: boolean
  notApplicableBefore: NotApplicableBefore | null
  /** The same movement on Top N and on the NPS score (Python's), when stamped. */
  topBoxes: LiftStatFigures | null
  nps: LiftStatFigures | null
  /** The Average movement's paired test (Python's, P2), when stamped. */
  meanTest: LiftStatTest | null
}

/**
 * The printed name of the test the active view's change takes, as Python
 * stamped it in the study language; none for NPS (no paired test is defined).
 */
function viewTestLabel(values: LiftRowValues, view: LiftStatView): string | null {
  if (view === 'topBoxes') {
    return values.topBoxes?.testLabel ?? null
  }
  if (view === 'mean') {
    return values.meanTest?.testLabel ?? null
  }
  return null
}

/** One movement as a view prints it: its triplet, direction and unit. */
interface LiftViewFigures {
  pre: number
  post: number
  delta: number
  direction: ScorecardDirection
  unit: string
}

function averageFigures(values: LiftRowValues, unit: string): LiftViewFigures {
  return { pre: values.pre, post: values.post, delta: values.delta, direction: values.direction, unit }
}

/**
 * Lookup only: the stamped figures behind the active view. A Top N change
 * reads in percentage points, an NPS change in points; Average is the row's
 * own triplet. Every shown row carries the active view (`offeredLiftViews`),
 * so the Average fallback is never reached on a drawn tab.
 */
function viewFigures(values: LiftRowValues, view: LiftStatView, unit: string): LiftViewFigures {
  const figures = view === 'topBoxes' ? values.topBoxes : view === 'nps' ? values.nps : null
  if (figures) {
    return {
      pre: figures.pre,
      post: figures.post,
      delta: figures.delta,
      direction: figures.direction,
      unit: view === 'topBoxes' ? 'percent_0_100' : NPS_POINTS_UNIT,
    }
  }
  return averageFigures(values, unit)
}

/** The views every shown row carries, in tab order (Average always). */
function offeredLiftViews(shown: Array<{ values: LiftRowValues }>): Array<LiftStatView> {
  const views: Array<LiftStatView> = ['mean']
  if (shown.length > 0 && shown.every(({ values }) => values.topBoxes != null)) {
    views.push('topBoxes')
  }
  if (shown.length > 0 && shown.every(({ values }) => values.nps != null)) {
    views.push('nps')
  }
  return views
}

/** "4 and 5" / "8, 9 and 10" in the viewer's language. */
function joinPoints(points: Array<number>, language: string): string {
  const names = points.map(String)
  try {
    return new Intl.ListFormat(language || 'en', { style: 'long', type: 'conjunction' }).format(names)
  } catch {
    return names.join(', ')
  }
}

function labeledOption(itemTags: Record<string, string>, id: string, label: string): string {
  const tag = itemTags[id]
  return tag ? `${tag}  ${label}` : label
}

function deltaChipClassName(direction: ScorecardDirection, lowBase: boolean): string {
  // A low base keeps its signed delta but not its verdict colour: the reader
  // is told to read the direction, not to trust the size.
  if (lowBase) {
    return 'bg-muted text-muted-foreground'
  }
  if (direction === 'up') {
    return 'bg-[hsl(var(--chart-positive))]/15 text-[hsl(var(--chart-positive))]'
  }
  if (direction === 'down') {
    return 'bg-[hsl(var(--chart-negative))]/15 text-[hsl(var(--chart-negative))]'
  }
  return 'bg-muted text-muted-foreground'
}

/**
 * The direction mark beside a change. Python stamps `direction` (flat is its
 * call, never the view's), so the arrow only restates it.
 */
const DIRECTION_ARROW: Record<ScorecardDirection, string> = {
  up: '↑',
  down: '↓',
  flat: '→',
}

function arrowClassName(direction: ScorecardDirection, lowBase: boolean): string {
  if (lowBase || direction === 'flat') {
    return 'text-muted-foreground'
  }
  return direction === 'up' ? 'text-[hsl(var(--chart-positive))]' : 'text-[hsl(var(--chart-negative))]'
}

function fromAsset(score: LiftScorecardAssetScore): LiftRowValues {
  return {
    pre: score.pre,
    post: score.post,
    delta: score.delta,
    direction: score.direction,
    pairs: score.pairs ?? null,
    lowBase: score.lowBase === true,
    notApplicableBefore: score.notApplicableBefore ?? null,
    topBoxes: score.views?.topBoxes ?? null,
    nps: score.views?.nps ?? null,
    meanTest: score.views?.meanTest ?? null,
  }
}

function tripletOf(figures: { pre: number; post: number; delta: number } | null | undefined): Array<[number, number, number]> {
  return figures ? [[figures.pre, figures.post, figures.delta]] : []
}

function rowTriplets(row: LiftScorecardRow, view: LiftStatView = 'mean'): Array<[number, number, number]> {
  if (view !== 'mean') {
    return [
      ...Object.values(row.byAssetId ?? {}).flatMap((score) => tripletOf(score.views?.[view])),
      ...tripletOf(row.views?.[view]),
    ]
  }
  const triplets = Object.values(row.byAssetId ?? {}).map(
    (score): [number, number, number] => [score.pre, score.post, score.delta],
  )
  if (row.pre != null && row.post != null && row.delta != null) {
    triplets.push([row.pre, row.post, row.delta])
  }
  return triplets
}

/**
 * Every triplet this row's lift grid can show, so the grid's decimals
 * (`liftDisplayDecimals`) are chosen over all of them: switching the selected
 * asset or grid row never changes how a figure prints, two measures of one
 * grid never print at different decimals, and the figures match the deck's
 * paired grid and the Summary writers (`paired_lift_grid_mean_decimals`).
 *
 * The grid key is Python's (catalogue `vocab.lift.display_decimals`): every
 * row drawn on a paired grid (a lane or a grid row) that reads the same post
 * question against the same baseline, whatever its unit. A row the deck draws
 * on no grid (a pooled single-row lift) is its own scope. A row stored before
 * `questionId`/`baselineQuestionId` existed has no grid key and keeps the
 * declared legacy scope: its own measure's triplets across its grid rows.
 */
function gridTriplets(
  row: LiftScorecardRow,
  rows: Array<LiftScorecardRow>,
  view: LiftStatView = 'mean',
): Array<[number, number, number]> {
  if (row.questionId != null && row.baselineQuestionId != null) {
    if (!drawnOnGrid(row)) {
      return rowTriplets(row, view)
    }
    return rows
      .filter((other) => drawnOnGrid(other) && sameGrid(other, row))
      .flatMap((other) => rowTriplets(other, view))
  }
  if (row.gridRowKey == null) {
    return rowTriplets(row, view)
  }
  return rows
    .filter((other) => other.gridRowKey != null && sameMeasure(other, row))
    .flatMap((other) => rowTriplets(other, view))
}

/** Python's `paired_grid_readings`: a lane or a grid row is drawn on a grid. */
function drawnOnGrid(row: LiftScorecardRow): boolean {
  return row.gridRowKey != null || Object.keys(row.byAssetId ?? {}).length > 0
}

function sameGrid(a: LiftScorecardRow, b: LiftScorecardRow): boolean {
  return a.questionId === b.questionId && a.baselineQuestionId === b.baselineQuestionId
}

function sameMeasure(a: LiftScorecardRow, b: LiftScorecardRow): boolean {
  if (a.metricId != null || b.metricId != null) {
    return a.metricId === b.metricId
  }
  return a.measureId === b.measureId && a.questionId === b.questionId
}

/** One grid row a card's lift was paired on (`gridRowKey`). */
interface GridRowOption {
  id: string
  label: string
  /** The declared tested subject (`gridRowRole`, `vocab.lift.row_role`). */
  tested: boolean
}

/**
 * The grid rows a card's lift was paired on (`gridRowKey`): the tested subject
 * (`gridRowRole`, `vocab.lift.row_role`) first, then the rest in the card's
 * own order, which is the grid's. Empty when no row names one.
 */
function gridRowOptions(rows: Array<LiftScorecardRow>): Array<GridRowOption> {
  const options: Array<GridRowOption> = []
  for (const row of rows) {
    if (row.gridRowKey == null || options.some((option) => option.id === row.gridRowKey)) {
      continue
    }
    options.push({
      id: row.gridRowKey,
      label: row.gridRowLabel ?? row.gridRowKey,
      tested: row.gridRowRole === 'tested_subject',
    })
  }
  return [...options.filter((option) => option.tested), ...options.filter((option) => !option.tested)]
}

interface ShownLiftRow {
  row: LiftScorecardRow
  values: LiftRowValues
}

/** The rows of one grid row, or (`key` null) the rows read on no grid row. */
interface GridRowGroup {
  key: string | null
  label: string
  tested: boolean
  items: Array<ShownLiftRow>
}

/**
 * Every shown row under its grid row, in `gridRowOptions` order (tested
 * subject first); rows read on no grid row come first, ungrouped. No grid
 * row is ever left out.
 */
function gridRowGroups(shown: Array<ShownLiftRow>, options: Array<GridRowOption>): Array<GridRowGroup> {
  const plain = shown.filter((item) => item.row.gridRowKey == null)
  const groups: Array<GridRowGroup> = plain.length > 0 ? [{ key: null, label: '', tested: false, items: plain }] : []
  for (const option of options) {
    const items = shown.filter((item) => item.row.gridRowKey === option.id)
    if (items.length > 0) {
      groups.push({ key: option.id, label: option.label, tested: option.tested, items })
    }
  }
  return groups
}

/**
 * Lookup only: every figure is Python's. A lane-merged row has no pooled
 * movement (lanes are never pooled), so it reads the selected asset, or its
 * one asset when there is only one. `null` when the row has nothing for this
 * selection, which the view renders as no entry.
 */
function liftRowValues(row: LiftScorecardRow, selectedAssetId: string | null): LiftRowValues | null {
  const byAsset = row.byAssetId ?? {}
  if (selectedAssetId && byAsset[selectedAssetId]) {
    return fromAsset(byAsset[selectedAssetId])
  }
  if (row.pre != null && row.post != null && row.delta != null && row.direction != null) {
    return {
      pre: row.pre,
      post: row.post,
      delta: row.delta,
      direction: row.direction,
      pairs: row.pairs ?? null,
      lowBase: row.lowBase === true,
      notApplicableBefore: row.notApplicableBefore ?? null,
      topBoxes: row.views?.topBoxes ?? null,
      nps: row.views?.nps ?? null,
      meanTest: row.views?.meanTest ?? null,
    }
  }
  const assetIds = Object.keys(byAsset)
  if (!selectedAssetId && assetIds.length === 1) {
    return fromAsset(byAsset[assetIds[0]])
  }
  return null
}

/**
 * The before/after card: one row per paired measure, Before, After and the
 * measured change. Shared by the Summary Featured slot and the Survey data
 * question card, so both surfaces print the same figures the same way.
 */
export function LiftView(props: LiftViewProps) {
  const { scorecard: card, itemTags, showHeading = true } = props

  const { t, i18n } = useTranslation('app')
  const assetOptions = card.assetOptions ?? []
  const hasPerAssetLift = card.rows.some(
    (row) => row.byAssetId != null && Object.keys(row.byAssetId).length > 0,
  )
  const selectable = assetOptions.filter((option) =>
    card.rows.some((row) => row.byAssetId != null && option.id in row.byAssetId),
  )
  const showSelector = selectable.length >= 2 && hasPerAssetLift
  const [selectedAssetId, setSelectedAssetId] = useState<string>(
    () => card.defaultAssetId ?? selectable[0]?.id ?? '',
  )
  const optionKey = selectable.map((option) => option.id).join('|')
  // Decision 20: the card opens on Python's default view (Top N, NPS, else
  // Average); a card stored before it opens on Average and draws no tabs.
  const [requestedView, setRequestedView] = useState<LiftStatView>(() => card.views?.defaultView ?? 'mean')
  // Bases, tests and the could-not-rate-before group sit behind one toggle:
  // the rows read lean, the detail stays one click away.
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  // A new card in the same slot opens on its own default view.
  useEffect(() => {
    setRequestedView(card.views?.defaultView ?? 'mean')
  }, [card.views?.defaultView])

  useEffect(() => {
    setSelectedAssetId((current) =>
      selectable.some((option) => option.id === current)
        ? current
        : (card.defaultAssetId ?? selectable[0]?.id ?? ''),
    )
    // optionKey is the stable identity of the selectable list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionKey, card.defaultAssetId])

  // A lift paired per grid row (a brand-lift grid with the tested brand and
  // its competitors) has one card row per grid row and nothing across them:
  // every grid row shows, the declared subject first, never one at a time.
  const rowOptions = gridRowOptions(card.rows)
  const activeAssetId = showSelector ? selectedAssetId : null
  const shownRows = card.rows.flatMap((row) => {
    const values = liftRowValues(row, activeAssetId)
    return values ? [{ row, values }] : []
  })
  const groups = gridRowGroups(shownRows, rowOptions)
  const offeredViews = offeredLiftViews(shownRows)
  const activeView: LiftStatView = offeredViews.includes(requestedView) ? requestedView : 'mean'
  const boxSizes = [
    ...new Set(shownRows.map(({ values }) => values.topBoxes?.boxSize).filter((n): n is number => n != null)),
  ].sort((a, b) => a - b)
  const statTabs =
    offeredViews.length >= 2 ? (
      <StatTabs
        views={offeredViews}
        activeView={activeView}
        labels={{
          average: t('studyReport.statTabAverage'),
          topBoxes: boxSizes.map((n) => t('studyReport.statTabTopN', { n })).join(' / ') || null,
          nps: t('studyReport.statTabNps'),
        }}
        onViewChange={setRequestedView}
        testIdPrefix="summary-lift-stat-tab-"
      />
    ) : null
  // "Top 2 (4 and 5)": the points the Top N view counts, named once above
  // the rows when every row counts the same ones, else on each row.
  function pointsLabel(figures: LiftStatFigures | null): string | null {
    if (!figures?.boxSize || !figures.topBoxesPoints?.length) {
      return null
    }
    return t('studyReport.liftTopBoxesPoints', {
      n: figures.boxSize,
      points: joinPoints(figures.topBoxesPoints, i18n.language),
    })
  }
  const rowPoints = activeView === 'topBoxes' ? shownRows.map(({ values }) => pointsLabel(values.topBoxes)) : []
  const sharedPoints = rowPoints.length > 0 && rowPoints.every((label) => label === rowPoints[0]) ? rowPoints[0] : null
  // The test behind the active view's change, named once under the rows when
  // every row takes the same one, else on each row.
  const rowTests = shownRows.map(({ values }) => viewTestLabel(values, activeView))
  const sharedTest = rowTests.length > 0 && rowTests.every((label) => label != null && label === rowTests[0]) ? rowTests[0] : null
  // One measure per grid row reads as one line per row, the row named on
  // the line and the measure once above: a long grid stays compact.
  const groupedMeasures = new Set(
    groups.flatMap((group) => (group.key === null ? [] : group.items.map((item) => item.row.label))),
  )
  const oneLinePerGridRow =
    groupedMeasures.size === 1 &&
    groups.every((group) => group.key !== null && group.items.length === 1)
  // Every grid row on the same pairs (and none with a could-not-rate line):
  // the base is stated once under the rows instead of on each.
  const sharedBase =
    rowOptions.length >= 2 &&
    shownRows.length >= 2 &&
    shownRows.every(
      ({ values }) =>
        values.pairs != null &&
        values.pairs === shownRows[0].values.pairs &&
        values.lowBase === shownRows[0].values.lowBase &&
        values.notApplicableBefore == null,
    )
      ? shownRows[0].values
      : null

  const assetSelector = showSelector ? (
    <SummaryItemSelector
      options={selectable.map((option) => ({
        id: option.id,
        label: labeledOption(itemTags, option.id, option.label),
      }))}
      selected={selectedAssetId}
      onChange={setSelectedAssetId}
      label={t('studyReport.itemSelect')}
    />
  ) : null

  const selector =
    assetSelector || statTabs ? (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {statTabs}
        {assetSelector}
      </div>
    ) : null

  // One rounding footnote for the card (plots plan F, kpi 3), raised when any
  // shown row's figures still do not reconcile at the grid's decimals.
  const needsRoundingNote = shownRows.some(({ row, values }) => {
    const figures = viewFigures(values, activeView, row.unit)
    return formatLiftTriplet(
      figures.pre,
      figures.post,
      figures.delta,
      figures.unit,
      gridTriplets(row, card.rows, activeView),
    ).roundingNote
  })
  // Python's paired-test floor. When stamped, a change resting on fewer pairs
  // is withheld and the row says why (plots plan F, kpi 2); cards stored
  // before it keep the signed change with its verdict colour dropped.
  const minPairs = card.minPairs ?? null

  function renderLiftRow(
    row: LiftScorecardRow,
    values: LiftRowValues,
    separated: boolean,
    gridRow: GridRowGroup | null,
  ) {
    const figures = viewFigures(values, activeView, row.unit)
    const liftFigures = formatLiftTriplet(
      figures.pre,
      figures.post,
      figures.delta,
      figures.unit,
      gridTriplets(row, card.rows, activeView),
    )
    const direction = figures.direction
    const ownPoints = activeView === 'topBoxes' && !sharedPoints ? pointsLabel(values.topBoxes) : null
    const ownTest = sharedTest ? null : viewTestLabel(values, activeView)
    const notApplicable = values.notApplicableBefore
    const withheld = values.lowBase && minPairs != null
    // Test ids name the grid row too: one measure repeats on every row.
    const idSuffix = row.gridRowKey != null ? `${row.measureId}-${row.gridRowKey}` : row.measureId
    const baseLine =
      !sharedBase && values.pairs != null
        ? t(
            notApplicable ? 'studyReport.liftBasePriorOpinion' : 'studyReport.liftBase',
            { count: values.pairs },
          )
        : null
    const notApplicableLine = notApplicable
      ? notApplicable.postMean != null
        ? t('studyReport.liftCouldNotRateBefore', {
            count: notApplicable.count,
            after: formatAbsoluteScore(notApplicable.postMean, row.unit),
          })
        : t('studyReport.liftCouldNotRateBeforeNoAfter', { count: notApplicable.count })
      : null
    return (
      <div
        key={`${row.metricId ?? row.measureId}:${row.gridRowKey ?? ''}`}
        data-testid={`summary-lift-row-${idSuffix}`}
        data-grid-row={row.gridRowKey ?? undefined}
      >
        {separated && <div className="border-t border-border/50 my-2" />}
        <div
          className="grid items-center gap-2"
          style={{ gridTemplateColumns: 'minmax(110px, 2.2fr) 1fr 1fr 1fr' }}
        >
          <Text
            size="sm"
            className={cn(
              'text-xs leading-snug',
              gridRow?.tested ? 'font-semibold text-foreground' : 'text-muted-foreground',
            )}
          >
            {gridRow ? gridRow.label : row.label}
          </Text>
          <span
            data-testid="summary-lift-pre"
            className="text-sm font-medium tabular-nums text-foreground text-center"
          >
            {liftFigures.pre}
          </span>
          <span
            data-testid="summary-lift-post"
            className="text-sm font-medium tabular-nums text-foreground text-center"
          >
            {liftFigures.post}
          </span>
          <div className="flex justify-center items-center gap-1">
            {withheld ? (
              <span
                data-testid={`summary-lift-withheld-${idSuffix}`}
                className="text-[11px] leading-snug text-muted-foreground text-center"
              >
                {t('studyReport.liftChangeWithheld', { count: values.pairs ?? 0, min: minPairs })}
              </span>
            ) : (
              <>
                <span
                  aria-hidden="true"
                  data-testid={`summary-lift-arrow-${idSuffix}`}
                  className={cn('text-sm font-semibold', arrowClassName(direction, values.lowBase))}
                >
                  {DIRECTION_ARROW[direction]}
                </span>
                <span
                  data-testid="summary-lift-delta"
                  data-low-base={values.lowBase ? 'true' : undefined}
                  className={cn(
                    'text-sm font-medium tabular-nums px-2 py-0.5 rounded-full',
                    deltaChipClassName(direction, values.lowBase),
                  )}
                >
                  {liftFigures.delta}
                </span>
              </>
            )}
          </div>
        </div>
        <div hidden={!isDetailsOpen} data-testid={`summary-lift-details-${idSuffix}`}>
        {ownPoints ? (
          <Text
            size="sm"
            data-testid={`summary-lift-view-points-${idSuffix}`}
            className="text-[11px] text-muted-foreground leading-snug mt-1"
          >
            {ownPoints}
          </Text>
        ) : null}
        {ownTest ? (
          <Text
            size="sm"
            data-testid={`summary-lift-view-test-${idSuffix}`}
            className="text-[11px] text-muted-foreground leading-snug mt-1"
          >
            {ownTest}
          </Text>
        ) : null}
        {baseLine ? (
          <Text
            size="sm"
            data-testid={`summary-lift-base-${idSuffix}`}
            className="text-[11px] text-muted-foreground leading-snug mt-1"
          >
            {baseLine}
          </Text>
        ) : null}
        {notApplicableLine ? (
          <Text
            size="sm"
            data-testid={`summary-lift-not-applicable-before-${idSuffix}`}
            className="text-[11px] text-muted-foreground leading-snug mt-1"
          >
            {notApplicableLine}
          </Text>
        ) : null}
        </div>
      </div>
    )
  }

  const hasRowDetails = shownRows.some(({ values }) => {
    const hasBase = !sharedBase && values.pairs != null
    const hasPoints = activeView === 'topBoxes' && !sharedPoints && pointsLabel(values.topBoxes) != null
    const hasTest = !sharedTest && viewTestLabel(values, activeView) != null
    return hasBase || hasPoints || hasTest || values.notApplicableBefore != null
  })
  const hasDetails = hasRowDetails || sharedBase != null || sharedTest != null || needsRoundingNote

  return (
    <div className="flex flex-col gap-3" data-testid="summary-lift-card" data-stat-view={activeView}>
      {(showHeading || selector) && (
        <div className="flex items-center justify-between gap-3">
          {showHeading ? (
            <Text size="sm" className="font-medium text-muted-foreground">
              {t('studyReport.lift')}
            </Text>
          ) : (
            <span />
          )}
          {selector}
        </div>
      )}
      {card.designNote && (
        <div className="flex items-start gap-1.5">
          <Text
            size="sm"
            className="text-xs text-muted-foreground leading-snug"
            data-testid="summary-card-lift-design-note"
          >
            {card.designNote}
          </Text>
          {card.designNoteHover ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid="summary-card-lift-design-note-info"
                    aria-label={t('studyReport.kpiFigureInfo')}
                    className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px] p-3 text-left">
                  <Text
                    size="sm"
                    data-testid="summary-card-lift-design-note-hover"
                    className="leading-snug"
                  >
                    {card.designNoteHover}
                  </Text>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </div>
      )}
      <div className="grid gap-2" style={{ gridTemplateColumns: 'minmax(110px, 2.2fr) 1fr 1fr 1fr' }}>
        {oneLinePerGridRow || sharedPoints ? (
          <span className="flex flex-col">
            {oneLinePerGridRow ? (
              <span
                className="text-[11px] font-semibold text-muted-foreground"
                data-testid="summary-lift-measure-label"
              >
                {[...groupedMeasures][0]}
              </span>
            ) : null}
            {sharedPoints ? (
              <span className="text-[10px] text-muted-foreground" data-testid="summary-lift-view-points">
                {sharedPoints}
              </span>
            ) : null}
          </span>
        ) : (
          <span />
        )}
        <span className="text-[11px] font-semibold text-muted-foreground text-center">
          {t('studyReport.before')}
        </span>
        <span className="text-[11px] font-semibold text-muted-foreground text-center">
          {t('studyReport.after')}
        </span>
        <span className="text-[11px] font-semibold text-muted-foreground text-center">Δ</span>
      </div>
      <div className="flex flex-col" data-testid="summary-lift-rows">
        {groups.map((group, groupIndex) => (
          <div
            key={group.key ?? '__rows'}
            className={cn('flex flex-col', groupIndex > 0 && !oneLinePerGridRow && 'mt-3')}
            data-testid={group.key === null ? 'summary-lift-rows-plain' : `summary-lift-grid-row-${group.key}`}
            data-tested-subject={group.tested ? 'true' : undefined}
          >
            {group.key !== null && !oneLinePerGridRow ? (
              <Text
                size="sm"
                className={cn(
                  'text-xs leading-snug mb-1',
                  group.tested ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground',
                )}
                data-testid="summary-lift-row-label"
              >
                {group.label}
              </Text>
            ) : null}
            {group.items.map(({ row, values }, index) =>
              renderLiftRow(
                row,
                values,
                index > 0 || (oneLinePerGridRow && groupIndex > 0),
                oneLinePerGridRow && group.key !== null ? group : null,
              ),
            )}
          </div>
        ))}
        <div hidden={!isDetailsOpen} data-testid="summary-lift-details-shared">
        {sharedBase ? (
          <Text
            size="sm"
            data-testid="summary-lift-base-shared"
            className="text-[11px] text-muted-foreground leading-snug mt-2"
          >
            {t('studyReport.liftBase', { count: sharedBase.pairs ?? 0 })}
          </Text>
        ) : null}
        {sharedTest ? (
          <Text
            size="sm"
            data-testid="summary-lift-view-test"
            className="text-[11px] text-muted-foreground leading-snug mt-2"
          >
            {sharedTest}
          </Text>
        ) : null}
        {needsRoundingNote ? (
          <Text
            size="sm"
            data-testid="summary-lift-rounding-note"
            className="text-[11px] text-muted-foreground leading-snug mt-2"
          >
            {t('studyReport.liftRoundingNote')}
          </Text>
        ) : null}
        </div>
        {hasDetails ? (
          <button
            type="button"
            data-testid="summary-lift-details-toggle"
            aria-expanded={isDetailsOpen}
            onClick={() => setIsDetailsOpen((isOpen) => !isOpen)}
            className="mt-2 inline-flex items-center gap-1 self-start text-xs text-muted-foreground hover:text-foreground"
          >
            {isDetailsOpen ? t('studyReport.liftDetailsHide') : t('studyReport.liftDetailsShow')}
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', isDetailsOpen && 'rotate-180')}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>
    </div>
  )
}
